import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminDownload,
  deleteAdminDownload,
  getAdminDownloads,
  removeAdminDownloadFile,
  updateAdminDownload,
  uploadAdminDownloadFile,
} from "../../../api/downloadsAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  title: "",
  language: "en",
  isActive: true,
};

const DownloadsManager = () => {
  const { i18n } = useTranslation();
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createFile, setCreateFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editFile, setEditFile] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminDownloads();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load downloads",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const sortedItems = useMemo(() => {
    const currentLang = String(i18n.language || "en").toLowerCase().split("-")[0];
    const filtered = items.filter(
      (item) =>
        String(item?.language || "en").toLowerCase().split("-")[0] === currentLang
    );
    return [...filtered].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [items, i18n.language]);

  const toPayload = (form) => ({
    title: String(form.title || "").trim(),
    language: String(form.language || "en").toLowerCase(),
    isActive: Boolean(form.isActive),
  });

  const handleCreate = async () => {
    if (!String(createForm.title || "").trim()) {
      notify({ type: "error", title: "Title is required" });
      return;
    }
    setCreating(true);
    try {
      const created = await createAdminDownload(toPayload(createForm));
      if (createFile && created?.id) {
        await uploadAdminDownloadFile({ id: created.id, file: createFile });
      }
      notify({ type: "success", title: "Download created" });
      setCreateForm(EMPTY_FORM);
      setCreateFile(null);
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      title: item?.title || "",
      language: String(item?.language || "en").toLowerCase().split("-")[0],
      isActive: typeof item?.isActive === "boolean" ? item.isActive : true,
    });
    setEditFile(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!String(editForm.title || "").trim()) {
      notify({ type: "error", title: "Title is required" });
      return;
    }
    try {
      await updateAdminDownload({ id: editingId, payload: toPayload(editForm) });
      if (editFile) {
        await uploadAdminDownloadFile({ id: editingId, file: editFile });
      }
      notify({ type: "success", title: "Download updated" });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      setEditFile(null);
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleDelete = async (item) => {
    const ok = window.confirm(`Delete download "${item?.title || item?.id}" completely?`);
    if (!ok) return;
    try {
      await deleteAdminDownload({ id: item.id });
      notify({ type: "success", title: "Download deleted" });
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const renderForm = ({ form, setForm, file, setFile }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700 sm:col-span-2">
        Title
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700">
        Language
        <select
          value={form.language}
          onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="en">EN</option>
          <option value="ar">AR</option>
          <option value="ku">KU</option>
        </select>
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-7">
        <input
          type="checkbox"
          checked={Boolean(form.isActive)}
          onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
        />
        Active
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        PDF File (optional)
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="mt-1 block w-full text-sm"
        />
        {file?.name ? <span className="mt-1 block text-xs text-slate-500">Selected: {file.name}</span> : null}
      </label>
    </div>
  );

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Downloads Manager</h2>
            <p className="text-sm text-slate-500 mt-1">Add, update, upload and remove PDF downloads.</p>
          </div>
          <button
            type="button"
            onClick={loadItems}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            Reload
          </button>
        </div>
        {renderForm({
          form: createForm,
          setForm: setCreateForm,
          file: createFile,
          setFile: setCreateFile,
        })}
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={creating}
            onClick={handleCreate}
            className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60"
          >
            {creating ? "Creating..." : "Create download"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Lang</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">File</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  Loading downloads...
                </td>
              </tr>
            ) : sortedItems.length ? (
              sortedItems.map((item) => {
                const fileUrl = resolveMediaUrl(item?.fileUrl);
                return (
                  <tr key={item.id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{item.title || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 uppercase">{item.language || "-"}</td>
                    <td className="px-3 py-3 text-sm">
                      {fileUrl ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-700 underline break-all"
                        >
                          Open PDF
                        </a>
                      ) : (
                        <span className="text-slate-500">No file</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${item.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          onChange={(e) =>
                            setUploadFiles((p) => ({ ...p, [item.id]: e.target.files?.[0] || null }))
                          }
                          className="text-xs"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            disabled={!uploadFiles[item.id]}
                            onClick={async () => {
                              try {
                                await uploadAdminDownloadFile({ id: item.id, file: uploadFiles[item.id] });
                                notify({ type: "success", title: "PDF uploaded" });
                                await loadItems();
                              } catch (error) {
                                notify({
                                  type: "error",
                                  title: "Upload failed",
                                  message: error?.response?.data?.message || error?.message || "",
                                });
                              }
                            }}
                            className="text-xs px-2 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                          >
                            Upload PDF
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await removeAdminDownloadFile({ id: item.id });
                                notify({ type: "success", title: "PDF removed" });
                                await loadItems();
                              } catch (error) {
                                notify({
                                  type: "error",
                                  title: "Remove file failed",
                                  message: error?.response?.data?.message || error?.message || "",
                                });
                              }
                            }}
                            className="text-xs px-2 py-1.5 rounded border border-amber-200 text-amber-700 hover:bg-amber-50"
                          >
                            Remove PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  No downloads found for this language.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Download #{editingId}</h3>
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setEditForm(EMPTY_FORM);
                setEditFile(null);
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          {renderForm({
            form: editForm,
            setForm: setEditForm,
            file: editFile,
            setFile: setEditFile,
          })}

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={saveEdit}
              className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm"
            >
              Save changes
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default DownloadsManager;
